import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { AngularSvgIconModule } from "angular-svg-icon";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { ApeTablePaginationComponent } from "../ape-table-pagination/ape-table-pagination.component";
import { ApeAdminImpersonatePlaceholderComponent } from "../ape-admin-impersonate-placeholder/ape-admin-impersonate-placeholder.component";
import { ApeInputComponent } from "../ape-input/ape-input.component";

import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { ImpersonifiableUserSummary } from "alloy-performance-engine-cl";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { TranslateService } from "src/app/util/translate.service";
import { ApeMenuComponent } from "../ape-menu/ape-menu.component";

@Component({
  selector: "ape-admin-impersonate-table",
  templateUrl: "./ape-admin-impersonate-table.component.html",
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    TooltipModule,
    AngularSvgIconModule,
    ApeButtonComponent,
    ApeAvatarComponent,
    ApeTablePaginationComponent,
    ApeAdminImpersonatePlaceholderComponent,
    ApeInputComponent,
    TranslatePipe,
    ApeMenuComponent
  ]
})
export class ApeAdminImpersonateTableComponent implements OnChanges {
  /**
   * Chiavi di traduzione per type safety
   */
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private translateService = inject(TranslateService);

  /**
   * Lista degli utenti da mostrare
   */
  @Input() users: ImpersonifiableUserSummary[] = [];

  /**
   * Valore della ricerca
   */
  @Input() searchValue = "";

  /**
   * Valore locale dell'input di ricerca
   */
  localSearchValue = "";

  /**
   * Menu items per le azioni
   */
  menuItems: MenuItem[] = [];

  /**
   * Riferimento al menu tiered
   */
  @ViewChild("apeMenu") apeMenu!: Menu;

  /**
   * Sincronizza il valore locale quando cambia l'input
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchValue && changes.searchValue.currentValue !== this.localSearchValue) {
      this.localSearchValue = changes.searchValue.currentValue as string;
    }
  }

  /**
   * Evento emesso quando cambia il valore della ricerca
   */
  @Output() searchChange = new EventEmitter<string>();

  /**
   * Evento emesso quando si impersona un employee
   */
  @Output() impersonateEmployee = new EventEmitter<ImpersonifiableUserSummary>();

  /**
   * Evento emesso quando si impersona un manager
   */
  @Output() impersonateManager = new EventEmitter<ImpersonifiableUserSummary>();

  /**
   * Evento emesso quando si esegue una ricerca
   */
  @Output() search = new EventEmitter<{
    page: number;
    pageSize: number;
    searchText?: string;
    isSearch?: boolean;
  }>();

  /**
   * Flag di loading per i dati
   */
  @Input() loading = false;

  /**
   * Pagina corrente per la paginazione
   */
  @Input() page = 1;

  /**
   * Dimensione pagina per la paginazione
   */
  @Input() pageSize = 50;

  /**
   * Numero totale di elementi
   */
  @Input() totalItems = 0;

  /**
   * Numero totale di pagine
   */
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  readonly pageSizeOptions: MenuItem[] = [
    {
      title: "10",
      id: "10"
    },
    {
      title: "20",
      id: "20"
    },
    {
      title: "50",
      id: "50"
    },
    {
      title: "100",
      id: "100"
    }
  ];

  readonly selectedPageSize: string = "50";

  /**
   * Utente selezionato per il menu azioni
   */
  selectedUserForActions: ImpersonifiableUserSummary | null = null;

  /**
   * Mappa dei feedback per la copia delle email (email -> timestamp)
   */
  copyFeedbackMap: Map<string, number> = new Map();

  /**
   * Gestisce il cambio della ricerca
   */
  onSearchChange(value: string): void {
    this.localSearchValue = value;
    this.searchChange.emit(value);
  }

  /**
   * Gestisce l'input event per la ricerca
   */
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.localSearchValue = target.value;
  }

  /**
   * Gestisce il key up event per la ricerca (triggera ricerca con Invio)
   */
  onSearchKeyUp(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.performSearch();
    }
  }

  /**
   * Esegue la ricerca
   */
  performSearch(): void {
    this.search.emit({
      page: 1,
      pageSize: this.pageSize,
      searchText: this.localSearchValue || undefined,
      isSearch: true
    });
  }

  /**
   * Gestisce il cambio di pagina
   */
  onPageChange(page: number): void {
    this.search.emit({
      page: page,
      pageSize: this.pageSize,
      searchText: this.localSearchValue || undefined,
      isSearch: false
    });
  }

  /**
   * Gestisce il cambio di dimensione pagina
   */
  onPageSizeChange(pageSize: number): void {
    this.search.emit({
      page: 1,
      pageSize: pageSize,
      searchText: this.localSearchValue || undefined,
      isSearch: false
    });
  }

  /**
   * Gestisce l'impersonazione dell'employee
   */
  onImpersonateEmployee(user: ImpersonifiableUserSummary): void {
    this.impersonateEmployee.emit(user);
    this.selectedUserForActions = null;
  }

  /**
   * Gestisce l'impersonazione del manager
   */
  onImpersonateManager(user: ImpersonifiableUserSummary): void {
    this.impersonateManager.emit(user);
    this.selectedUserForActions = null;
  }

  canToggleActionMenu(user: ImpersonifiableUserSummary): boolean {
    return user.canImpersonateUser || user.canImpersonateManager;
  }

  /**
   * Apre il menu azioni per l'utente specificato
   */
  toggleActionMenu(user: ImpersonifiableUserSummary, event: Event): void {
    this.selectedUserForActions = user;
    this.menuItems = [];

    if (user.canImpersonateUser) {
      this.menuItems.push({
        label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.TABLE.IMPERSONATE_EMPLOYEE),
        icon: "assets/icons/user.svg",
        command: () => {
          this.onImpersonateEmployee(user);
        },
        escape: false
      });
    }
    if (user.canImpersonateManager) {
      this.menuItems.push({
        label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.TABLE.IMPERSONATE_MANAGER),
        icon: "assets/icons/users-round.svg",
        command: () => {
          this.onImpersonateManager(user);
        },
        escape: false
      });
    }
    if (this.menuItems.length > 0) {
      this.apeMenu.toggle(event);
    }
  }

  /**
   * Copia l'email negli appunti
   */
  async copyEmail(email: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(email);
      this.copyFeedbackMap.set(email, Date.now());
      setTimeout(() => {
        this.copyFeedbackMap.delete(email);
      }, 2000);
    } catch (error) {
      // Silently fail - clipboard operations can fail due to browser permissions
    }
  }

  /**
 * Verifica se l'email è stata copiata di recente
 */
  isEmailCopied(email: string): boolean {
    return this.copyFeedbackMap.has(email);
  }
}
